import axios from 'axios';

export const downloadFileFromUrl = (url?: string, filename?: string) => {
  if (!url) {
    return;
  }

  // Determine filename if not provided
  const downloadFilename = filename || (() => {
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1] || 'download';
  })();

  // Helper function to trigger download from blob
  const downloadBlob = (blob: Blob) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = downloadFilename;
    anchor.setAttribute('download', downloadFilename);
    anchor.style.position = 'fixed';
    anchor.style.top = '-1000px';
    anchor.style.left = '-1000px';
    anchor.style.visibility = 'hidden';
    
    document.body.appendChild(anchor);
    anchor.click();
    
    // Clean up after download
    setTimeout(() => {
      if (document.body.contains(anchor)) {
        document.body.removeChild(anchor);
      }
      window.URL.revokeObjectURL(blobUrl);
    }, 200);
  };

  // Always use fetch to get blob - this ensures download instead of opening
  const downloadFile = async () => {
    // Try fetch first - this works for same-origin or CORS-enabled URLs
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      // Download the blob
      downloadBlob(blob);
      return; // Success, exit early
      
    } catch (fetchError: any) {
      console.error('Error downloading file via fetch:', fetchError);
      
      // If fetch fails (likely CORS issue), try XMLHttpRequest
      // XHR sometimes works even when fetch doesn't due to different CORS handling
      try {
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          let xhrCompleted = false;
          
          xhr.open('GET', url, true);
          xhr.responseType = 'blob';
          
          xhr.onload = function() {
            if (xhrCompleted) return;
            xhrCompleted = true;
            
            if (xhr.status === 200) {
              try {
                const blob = xhr.response;
                if (blob && blob.size > 0) {
                  downloadBlob(blob);
                  resolve();
                } else {
                  reject(new Error('XHR returned empty blob'));
                }
              } catch (blobError) {
                console.error('Error creating blob from XHR response:', blobError);
                reject(blobError);
              }
            } else {
              reject(new Error(`XHR failed with status: ${xhr.status}`));
            }
          };
          
          xhr.onerror = function() {
            if (xhrCompleted) return;
            xhrCompleted = true;
            reject(new Error('XHR request failed (likely CORS issue)'));
          };
          
          xhr.ontimeout = function() {
            if (xhrCompleted) return;
            xhrCompleted = true;
            reject(new Error('XHR request timed out'));
          };
          
          // Set timeout to prevent hanging
          xhr.timeout = 15000; // 15 seconds
          
          try {
            xhr.send();
          } catch (sendError) {
            xhrCompleted = true;
            reject(sendError);
          }
        });
        
        // If we get here, XHR succeeded
        return;
        
      } catch (xhrError: any) {
        console.error('XHR download also failed:', xhrError);
        
        // Try axios as third method (sometimes handles CORS differently)
        try {
          // Create axios instance without auth headers (for CDN URLs)
          const downloadAxios = axios.create({
            timeout: 30000,
          });
          
          // Remove any auth headers
          downloadAxios.interceptors.request.use((config) => {
            delete config.headers['Authorization'];
            delete config.headers['authorization'];
            return config;
          });
          
          const response = await downloadAxios.get(url, {
            responseType: 'blob',
          });
          
          if (response.data && response.data.size > 0) {
            downloadBlob(response.data);
            return; // Success
          } else {
            throw new Error('Axios returned empty blob');
          }
        } catch (axiosError: any) {
          console.error('Axios download also failed:', axiosError);
          
          // All methods failed due to CORS - this is a CDN configuration issue
          // As a fallback, open in new tab so user can manually download
          // This is better UX than just showing an error
          try {
            const newWindow = window.open(url, '_blank');
            if (newWindow) {
              // File opened in new tab - user can use browser's download option
              // Show a brief, non-blocking message
              console.warn('Download failed due to CORS. File opened in new tab. User can right-click and select "Save As" to download.');
              
              // Optional: Show a toast/notification instead of alert (less intrusive)
              // For now, we'll just open in new tab silently
            } else {
              // Pop-up blocked - show alert with instructions
              alert('Unable to download file automatically due to browser security restrictions (CORS issue).\n\nPlease:\n1. Allow pop-ups for this site, or\n2. Right-click the file link and select "Save As"\n\nNote: The CDN needs to enable CORS headers for automatic downloads to work.');
            }
          } catch (openError) {
            console.error('Failed to open file in new tab:', openError);
            alert('Unable to download file. This is likely a CDN CORS configuration issue.\n\nPlease contact support or configure the CDN to enable CORS headers for file downloads.');
          }
        }
      }
    }
  };

  downloadFile();
};

